import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, Zap, ChevronDown, LayoutGrid } from "lucide-react";
import { categories, searchTools } from "@/lib/toolsData";
import { ThemeToggle } from "@/components/ThemeToggle";

const featured = ["pdf-tools", "file-converters", "converters", "calculators", "developer"];

export const Header = () => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const catRef = useRef(null);
  const results = searchTools(q).slice(0, 7);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (tool) => {
    setOpen(false); setQ("");
    if (tool.status === "live") navigate(`/tool/${tool.slug}`);
    else navigate(`/category/${tool.category}`);
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-card/70 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0 group">
          <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-white ring-1 ring-border transition-transform duration-200 group-hover:scale-105">
            <img src="/kovi-emblem.png" alt="Kovi Tools" className="h-8 w-8 object-contain" />
          </span>
          <span className="font-heading text-xl font-black tracking-tighter">KOVI<span className="text-primary">.</span>TOOLS</span>
        </Link>

        <div ref={boxRef} className="relative ml-auto w-full max-w-sm hidden sm:block">
          <div className="flex items-center border border-border bg-card focus-within:border-primary transition-colors">
            <Search className="ml-3 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="search-tool-input"
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search tools…"
              className="w-full bg-transparent px-3 py-2.5 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          {open && results.length > 0 && (
            <div className="absolute mt-1 w-full border border-border bg-card shadow-lg">
              {results.map((t) => (
                <button key={t.slug} data-testid={`search-result-${t.slug}`} onClick={() => go(t)} className="flex w-full items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-left last:border-0 hover:bg-muted">
                  <span className="text-sm font-medium">{t.name}</span>
                  {t.status === "soon" && <span className="overline text-[10px] text-muted-foreground">Soon</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <nav className="hidden items-center gap-1 lg:flex shrink-0">
          <div ref={catRef} className="relative">
            <button data-testid="nav-all-tools" onClick={() => setCatOpen((v) => !v)} className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">
              <LayoutGrid className="h-4 w-4" /> All Tools <ChevronDown className="h-3 w-3" />
            </button>
            {catOpen && (
              <div className="absolute right-0 mt-1 grid w-[34rem] grid-cols-2 gap-1 border border-border bg-card p-2 shadow-xl">
                {categories.map((c) => (
                  <Link key={c.id} to={`/category/${c.id}`} onClick={() => setCatOpen(false)} data-testid={`nav-cat-${c.id}`} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted">
                    <c.icon className="h-4 w-4 text-primary" /> {c.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          {featured.map((id) => {
            const c = categories.find((x) => x.id === id);
            return <Link key={id} to={`/category/${id}`} data-testid={`nav-${id}`} className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">{c.name}</Link>;
          })}
          <Link to="/blog" data-testid="nav-blog" className="px-3 py-2 text-sm font-medium text-foreground hover:text-primary transition-colors">Blog</Link>
        </nav>

        <div className="ml-1 hidden lg:block"><ThemeToggle /></div>

        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <ThemeToggle />
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen((v) => !v)}>
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="max-h-[70vh] overflow-y-auto border-t border-border bg-card px-4 py-3 lg:hidden">
          {categories.map((c) => (
            <Link key={c.id} to={`/category/${c.id}`} onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 text-sm font-medium">
              <c.icon className="h-4 w-4 text-primary" /> {c.name}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};
